import React, { useEffect, useState } from 'react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@tempmee/react';
import history from '../../../history';
import { clearDataRange, fetchJobs, reloadJobs } from '../../../actions/jobsAction';
import { fetchRequestedEducations } from '../../../actions/licensesAction';
import { getAdminRamUser } from '../../../actions/userActions';
import { createLoadingSelector } from '../../../apis/selectors';
import { Table } from '../../commonComponents/Table/Table';
import { jobListColumn, jobListConditionalRowStyles } from '../../commonComponents/jobs-column';
import { handleFilterChange } from '../../commonComponents/Table/utils';
import { JobFilters } from '../../commonComponents/filters';
import { ListPage, ListPageTotal } from '../../commonComponents/ListPage';

const ROWS_PER_PAGE = 15;

const initialActions = ['GET_ADMIN_RAM', 'GET_DSO_NAMES'];
const initialLoadingSelector = createLoadingSelector(initialActions);

const actions = ['FETCH_JOBS', 'RELOAD_JOBS'];
const loadingSelector = createLoadingSelector(actions);

export const JobList = ({ payOutOnly }) => {
  const dispatch = useDispatch();

  const initialLoading = useSelector((state) => initialLoadingSelector(state));
  const isLoading = useSelector((state) => loadingSelector(state));
  const jobs = useSelector((state) => state.jobs.jobs);
  const searchParams = useSelector((state) => state.jobs.searchParams);
  const totalFiltered = useSelector((state) => state.jobs.totalFiltered);
  const totalJobs = useSelector((state) => state.jobs.total);

  const [filter, setFilter] = useState({
    profession: searchParams?.profession || '',
    subProfession: searchParams?.subProfession || '',
    specialtyRdh: searchParams?.specialtyRdh || '',
    specialtyDa: searchParams?.specialtyDa || '',
    workerClassification: searchParams?.workerClassification || '',
    ram: searchParams?.ram || '',
    dsoName: searchParams?.dsoName || '',
    dsoManager: searchParams?.dsoManager || '',
    searchText: searchParams?.searchText || '',
    state: searchParams?.state || '',
    jobStatus: searchParams?.jobStatus || 'all',
    dateRange: {
      startDate: searchParams?.startDate,
      endDate: searchParams?.endDate,
      key: 'selection',
    },
    sizePerPage: ROWS_PER_PAGE,
    page: searchParams?.page || 1,
    sortOrder: searchParams?.sortOrder || 'desc',
    sortField: searchParams?.sortField || 'createdAt',
    dsoRamIds: searchParams?.dsoRamIds || '',
    dsoId: searchParams?.dsoId || '',
  });

  const totalPages = Math.ceil(totalFiltered / ROWS_PER_PAGE);

  const handleOnSort = (selectedColumn, sortDirection) => {
    const sortObj = {
      page: filter.page,
      sortField: selectedColumn.sortField,
      sortOrder: sortDirection,
    };

    setFilter((prevState) => ({
      ...prevState,
      ...sortObj,
    }));
  };

  useEffect(() => {
    dispatch(getAdminRamUser());
    dispatch(fetchRequestedEducations());
  }, [dispatch]);

  const handleTableChange = (page) => {
    setFilter((prevState) => ({ ...prevState, page }));
  };

  useEffect(() => {
    dispatch(
      fetchJobs({
        ...filter,
        ...filter.dateRange,
      }),
    );
  }, [filter, dispatch]);

  const getJobUrl = (job) => `/job/${job.id}`;

  const getUserUrl = (userId, userType) => `/user/${userType.toLowerCase()}/${userId}`;

  const getColumn = () =>
    jobListColumn({
      getUserUrl,
      getJobUrl,
      showCounterExpiration: ['pending', 'counter', 'pending-counter'].includes(filter?.jobStatus),
      cancellationTime: true,
    });

  return (
    <ListPage
      title={payOutOnly ? 'Pay Out Jobs' : 'Shifts'}
      isLoading={isLoading}
      isInitialLoading={initialLoading}
      actions={(
        <>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => history.push('/multiday/post')}
          >
            Post Multi-Day
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => history.push('/jobcreate')}
          >
            Post a Job
          </Button>
        </>
      )}
      meta={(
        <>
          <ListPageTotal value={totalJobs} />
          <ListPageTotal label={`Total ${filter.jobStatus}`} value={totalFiltered} />
        </>
      )}
      filters={(
        <JobFilters
          filter={filter}
          handleFilterChange={(type, value) => handleFilterChange(type, value, setFilter)}
          payOutOnly={payOutOnly}
          appliedRange={searchParams}
          onDateRangeCleared={() => {
            dispatch(clearDataRange());
            dispatch(reloadJobs());
          }}
        />
      )}
    >
      <Table
        keyField="jobList.id"
        initialPage={filter.page}
        columns={getColumn()}
        data={jobs}
        noDataComponent="There are no Jobs"
        isLoading={isLoading}
        totalPages={totalPages}
        showTotal={false}
        onSort={handleOnSort}
        paginationPerPage={ROWS_PER_PAGE}
        paginationTotalRows={totalFiltered}
        onPageChange={handleTableChange}
        conditionalRowStyles={jobListConditionalRowStyles}
      />
    </ListPage>
  );
};
